/**
 * Folio View Dialog Component
 * Shows guest's complete booking and payment history
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  User, Mail, Phone, Calendar, Bed, DollarSign, 
  CreditCard, Receipt, CheckCircle, Clock, Loader2,
  MapPin, Hash
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export const FolioViewDialog = ({ open, onOpenChange, guest, hotelId }) => {
  const [loading, setLoading] = useState(false);
  const [folioData, setFolioData] = useState(null);

  useEffect(() => {
    if (open && guest) {
      loadFolioData();
    }
  }, [open, guest]);

  const loadFolioData = async () => {
    const guestEmail = guest?.guest_email || guest?.email;
    
    if (!guestEmail) {
      toast.error('Guest email not found');
      return;
    }
    
    const hotelIdToUse = guest?.hotel_id || hotelId;
    
    if (!hotelIdToUse) {
      toast.error('Hotel ID not found');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/pms/${hotelIdToUse}/guest-folio?email=${encodeURIComponent(guestEmail)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFolioData(data);
    } catch (error) {
      console.error('Error loading folio:', error);
      toast.error('Failed to load guest folio');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { variant: 'default', label: 'Confirmed', className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
      checked_in: { variant: 'default', label: 'Checked In', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
      checked_out: { variant: 'secondary', label: 'Checked Out', className: 'bg-gray-500/10 text-gray-600 border-gray-500/20' },
      cancelled: { variant: 'destructive', label: 'Cancelled', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
      pending: { variant: 'secondary', label: 'Pending', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const calculateTotalRevenue = () => {
    if (!folioData || !folioData.bookings) return 0;
    return folioData.bookings
      .filter(b => ['confirmed', 'checked_in', 'checked_out'].includes(b.booking_status))
      .reduce((sum, b) => sum + (b.final_price || 0), 0);
  };

  const calculateTotalNights = () => {
    if (!folioData || !folioData.bookings) return 0;
    return folioData.bookings
      .filter(b => ['confirmed', 'checked_in', 'checked_out'].includes(b.booking_status))
      .reduce((sum, b) => sum + (b.nights || 0), 0);
  };

  if (!guest) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Guest Folio - {guest.guest_name || 'Guest'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !folioData ? (
          <Alert>
            <AlertDescription>No folio data available</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-6">
            {/* Guest Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Guest Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="font-medium">{folioData.guest?.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{folioData.guest?.email || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{folioData.guest?.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Total Bookings</p>
                      <p className="font-medium">{folioData.bookings?.length || 0}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Folio Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs text-muted-foreground">Total Revenue</p>
                  </div>
                  <p className="text-2xl font-bold heading-font text-emerald-600">
                    ${calculateTotalRevenue().toFixed(2)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <p className="text-xs text-muted-foreground">Total Nights</p>
                  </div>
                  <p className="text-2xl font-bold heading-font text-blue-600">
                    {calculateTotalNights()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Bed className="w-4 h-4 text-purple-600" />
                    <p className="text-xs text-muted-foreground">Active Bookings</p>
                  </div>
                  <p className="text-2xl font-bold heading-font text-purple-600">
                    {folioData.bookings?.filter(b => 
                      ['confirmed', 'checked_in'].includes(b.booking_status)
                    ).length || 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Booking History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Booking History</CardTitle>
              </CardHeader>
              <CardContent>
                {!folioData.bookings || folioData.bookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No booking history found
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-32">Booking ID</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Room Type</TableHead>
                          <TableHead className="text-center">Nights</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {folioData.bookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell className="font-mono text-xs">
                              {booking.id?.substring(0, 12)}...
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="flex flex-col">
                                <span>{booking.check_in}</span>
                                <span className="text-xs text-muted-foreground">
                                  to {booking.check_out}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {booking.room_name || booking.room_type || 'N/A'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                                {booking.nights || 0}n
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              ${booking.final_price?.toFixed(2) || '0.00'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-xs">
                                <CreditCard className="w-3 h-3" />
                                {booking.payment_method || 'N/A'}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {getStatusBadge(booking.booking_status)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reliability Score (if available) */}
            {folioData.reliability && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Reliability Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold heading-font">
                        {folioData.reliability.score || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {folioData.reliability.tier || 'Standard'} Guest
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">
                        Completed: {folioData.reliability.completed_bookings || 0}
                      </p>
                      <p className="text-muted-foreground">
                        Cancelled: {folioData.reliability.cancelled_bookings || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
